import { z } from 'zod'
import { slugify } from '../slug'
import { subscriptionTierSchema } from './subscription'

export const PARTNER_NAME_MAX_LENGTH = 120
export const PARTNER_NOTE_MAX_LENGTH = 1000
export const PARTNER_CODE_MAX_LENGTH = 40
/** The query parameter a partner link carries on the business dashboard. */
export const PARTNER_QUERY_PARAM = 'partner'
/** How long a stored partner code still counts for a sign-up after the first visit. */
export const PARTNER_ATTRIBUTION_DAYS = 60

const PARTNER_CODE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/

export const partnerKindValues = ['person', 'organisation'] as const
export type PartnerKind = (typeof partnerKindValues)[number]

/** A partner code in canonical form, or null when the value cannot be one. */
export function normalizePartnerCode(value: string | null | undefined): string | null {
  if (!value) return null
  const code = value.trim().toLowerCase()
  return PARTNER_CODE_PATTERN.test(code) ? code : null
}

/** A partner code derived from a name: lowercase words joined by dashes, at most 40 characters. */
export function partnerCodeFromName(name: string): string {
  return slugify(name).slice(0, PARTNER_CODE_MAX_LENGTH).replace(/-+$/, '')
}

export const partnerCodeSchema = z.string().trim().toLowerCase().regex(PARTNER_CODE_PATTERN)

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => value || null)

export const createPartnerSchema = z.object({
  name: z.string().trim().min(1).max(PARTNER_NAME_MAX_LENGTH),
  kind: z.enum(partnerKindValues),
  note: optionalText(PARTNER_NOTE_MAX_LENGTH),
  contactEmail: z
    .string()
    .trim()
    .email()
    .max(254)
    .nullish()
    .transform((value) => value ?? null),
  /** Omitted: derived from the name. */
  code: partnerCodeSchema.optional(),
})

export const updatePartnerSchema = createPartnerSchema.omit({ code: true })

export const assignBusinessPartnerSchema = z.object({
  partnerId: z.string().uuid().nullable(),
})

const count = () => z.number().int().nonnegative()

/**
 * What the businesses a partner brought in have done. `activeBusinesses` counts those
 * that published a listing or event or posted a menu in the last 30 days.
 */
export const partnerStatsSchema = z.object({
  businesses: count(),
  activeBusinesses: count(),
  menuScans: count(),
  lunchMenus: count(),
  eventsPublished: count(),
  paidBusinesses: count(),
})

export const partnerSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  kind: z.enum(partnerKindValues),
  note: z.string().nullable(),
  contactEmail: z.string().nullable(),
  url: z.string().url(),
  createdAt: z.string().datetime(),
  stats: partnerStatsSchema,
})

export const partnerListResponseSchema = z.array(partnerSchema)

export const partnerBusinessSchema = z.object({
  id: z.string().uuid(),
  companyName: z.string(),
  signedUpAt: z.string().datetime(),
  referredAt: z.string().datetime().nullable(),
  subscriptionTier: subscriptionTierSchema,
  paid: z.boolean(),
  active: z.boolean(),
  menuScans: count(),
  lunchMenus: count(),
  eventsPublished: count(),
})

export const partnerDetailResponseSchema = z.object({
  partner: partnerSchema,
  businesses: z.array(partnerBusinessSchema),
})

export type CreatePartner = z.input<typeof createPartnerSchema>
export type UpdatePartner = z.input<typeof updatePartnerSchema>
export type PartnerStats = z.infer<typeof partnerStatsSchema>
export type Partner = z.infer<typeof partnerSchema>
export type PartnerBusiness = z.infer<typeof partnerBusinessSchema>
export type PartnerDetail = z.infer<typeof partnerDetailResponseSchema>
